import React, { useEffect, useState, useMemo } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, ExpressCheckoutElement, useStripe, useElements } from '@stripe/react-stripe-js';
import api from '../lib/api';
import { getAnonId, trackEvent } from '../lib/tracker';

const STRIPE_PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const stripePromise = STRIPE_PUBLISHABLE_KEY ? loadStripe(STRIPE_PUBLISHABLE_KEY) : null;

/**
 * Inner button — mounted only once the Elements provider has a client_secret.
 * Handles the wallet tap, SetupIntent confirmation and server-side subscription
 * creation (with 7-day trial for new users, no trial for returning subscribers).
 */
const ExpressButtonInner = ({ setupIntentId, onSuccess, onError }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [confirming, setConfirming] = useState(false);
  const [available, setAvailable] = useState(true);

  const handleConfirm = async () => {
    if (!stripe || !elements) return;
    setConfirming(true);
    trackEvent('express_checkout_click', {}, { allowDuplicate: true });

    // 1. Confirm the SetupIntent — wallet UI closes only after biometric auth.
    const { error } = await stripe.confirmSetup({
      elements,
      confirmParams: { return_url: `${window.location.origin}/dashboard` },
      redirect: 'if_required',
    });
    if (error) {
      setConfirming(false);
      trackEvent('express_checkout_failed', { code: error.code || 'unknown' }, { allowDuplicate: true });
      onError?.(error.message || 'No pudimos completar el pago con tu wallet');
      return;
    }

    // 2. Ask the backend to finish: attach PM, create subscription, sign-in.
    try {
      const res = await api.post(
        '/subscriptions/express-confirm',
        { setup_intent_id: setupIntentId, anon_id: getAnonId() },
        { withCredentials: true },
      );
      if (res.data?.session_token) {
        try { localStorage.setItem('wtpl_session_token', res.data.session_token); } catch { /* ignore quota */ }
      }
      trackEvent('checkout_completed', { flow: 'express', is_trial: !!res.data?.is_trial }, { allowDuplicate: true });
      onSuccess?.(res.data);
    } catch (e) {
      setConfirming(false);
      const detail = e?.response?.data?.detail || 'Error al activar tu suscripción';
      onError?.(detail);
    }
  };

  return (
    <div data-testid="express-checkout-container" className="w-full">
      <ExpressCheckoutElement
        onReady={(evt) => {
          // If no wallet is available on this device, hide the whole widget.
          const paymentMethods = evt?.availablePaymentMethods || {};
          const anyAvailable = Object.values(paymentMethods).some(Boolean);
          setAvailable(!!anyAvailable);
        }}
        onConfirm={handleConfirm}
        options={{
          buttonHeight: 48,
          buttonTheme: {
            applePay: 'black',
            googlePay: 'black',
          },
          paymentMethods: {
            applePay: 'always',
            googlePay: 'always',
            link: 'auto',
            paypal: 'never',
            amazonPay: 'never',
          },
          layout: { maxColumns: 1, maxRows: 3 },
        }}
      />
      {confirming && (
        <p className="text-center text-xs text-[#00D4FF] mt-2" data-testid="express-checkout-processing">
          Activando tu suscripción...
        </p>
      )}
      {!available && (
        // Fallback text when Apple Pay / Google Pay / Link aren't available on the current device.
        <p className="text-center text-[11px] text-[#666] mt-2">
          Wallets no disponibles en este dispositivo — usa el botón principal.
        </p>
      )}
    </div>
  );
};

/**
 * Wraps <Elements> and fetches a SetupIntent on mount so the express buttons
 * render as fast as possible. Renders nothing if the publishable key is missing
 * so we never show a broken widget in preview environments.
 */
const ExpressCheckoutButton = ({ onSuccess, onError, className = '' }) => {
  const [clientSecret, setClientSecret] = useState(null);
  const [setupIntentId, setSetupIntentId] = useState(null);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    if (!STRIPE_PUBLISHABLE_KEY) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.post('/subscriptions/express-setup', { anon_id: getAnonId() });
        if (cancelled) return;
        setClientSecret(res.data.client_secret);
        setSetupIntentId(res.data.setup_intent_id);
      } catch (e) {
        if (!cancelled) setLoadError(e?.response?.data?.detail || 'No pudimos preparar el pago express');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const options = useMemo(
    () => (clientSecret ? {
      clientSecret,
      appearance: {
        theme: 'night',
        variables: {
          colorPrimary: '#00D4FF',
          colorBackground: '#0D0D0D',
          colorText: '#FFFFFF',
        },
      },
    } : null),
    [clientSecret],
  );

  if (!STRIPE_PUBLISHABLE_KEY || !stripePromise) return null;
  if (loadError) return null; // Silently hide — main CTA stays as fallback
  if (!clientSecret || !options) {
    return (
      <div className={`h-12 rounded-md bg-white/[0.03] border border-white/10 animate-pulse ${className}`} />
    );
  }

  return (
    <div className={className} data-testid="express-checkout-widget">
      <Elements stripe={stripePromise} options={options}>
        <ExpressButtonInner
          setupIntentId={setupIntentId}
          onSuccess={onSuccess}
          onError={onError}
        />
      </Elements>
    </div>
  );
};

export default ExpressCheckoutButton;
